import React, { useState, useEffect, useContext } from 'react'
import {
    Container, Card, CardBody, Row, Col,
    Table, Button, UncontrolledDropdown,
    DropdownToggle, DropdownMenu, DropdownItem
} from 'reactstrap'
import { Link, useHistory } from 'react-router-dom'
import Moment from 'react-moment'
import CurrencyFormat from 'react-currency-format'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

import Breadcrumbs from '../../components/Global/Breadcrumbs'
import CancelacionCFDIModal from '../../components/CancelacionCFDIModal'
import ApiService from '../../data/services/ApiService'
import { cancelarComplementoConValidacion } from '../../helpers/cancelacionCFDI'
import { GeneralContext } from '../../context/'

const ComplementosIndex = () => {
    const { setLoading } = useContext(GeneralContext)
    const history = useHistory()

    const [complementos, setComplementos] = useState([])
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)
    const [cancelModalOpen, setCancelModalOpen] = useState(false)
    const [complementoToCancel, setComplementoToCancel] = useState(null)
    const [cancelReason, setCancelReason] = useState()
    const [folioSustitucion, setFolioSustitucion] = useState('')
    const [showFolioSustitucion, setShowFolioSustitucion] = useState(false)
    const [expandedId, setExpandedId] = useState(null)
    const [bitacoraMap, setBitacoraMap] = useState({})
    const limit = 20

    useEffect(() => {
        (async () => {
            await _load()
        })()
        // eslint-disable-next-line
    }, [page])

    const _load = async () => {
        setLoading(true)
        try {
            const response = await ApiService.getComplementos({ page, limit })
            const { data, pagination } = response.data
            setComplementos(data || [])
            setTotal(pagination?.total || 0)
            setTotalPages(pagination?.totalPages || 1)
        } catch (err) {
            toastr.error('No se pudieron cargar los complementos de pago')
        } finally {
            setLoading(false)
        }
    }

    const _downloadPDF = async (id, uuid) => {
        try {
            await ApiService.downloadComplementoPDF(id, uuid)
        } catch (err) {
            toastr.error('Error al descargar el PDF')
        }
    }

    const _downloadXML = async (id, uuid) => {
        try {
            await ApiService.downloadComplementoXML(id, uuid)
        } catch (err) {
            toastr.error('Error al descargar el XML')
        }
    }

    const _openCancelModal = (complemento) => {
        setComplementoToCancel(complemento)
        setCancelReason()
        setFolioSustitucion('')
        setShowFolioSustitucion(false)
        setCancelModalOpen(true)
    }

    const _toggleHistory = async (id) => {
        if (expandedId === id) {
            setExpandedId(null)
            return
        }
        setExpandedId(id)
        if (!bitacoraMap[id]) {
            try {
                const res = await ApiService.getBitacora({ complementoId: id, limit: 5 })
                setBitacoraMap(prev => ({ ...prev, [id]: res.data.data.eventos || [] }))
            } catch (e) {
                setBitacoraMap(prev => ({ ...prev, [id]: [] }))
            }
        }
    }

    const _verEnEgresos = async (complemento) => {
        try {
            const response = await ApiService.getEgresoByUuid(complemento.uuid)
            const egreso = response.data?.data?.egreso
            if (egreso) {
                history.push(`/egresos/${egreso._id}/detalle`)
            } else {
                toastr.warning('No se encontró el egreso correspondiente a este complemento.')
            }
        } catch (err) {
            toastr.error('No pude buscar el egreso.')
        }
    }

    const _verifySatStatus = async (complemento) => {
        try {
            setLoading(true)
            const response = await ApiService.verificarComplementoSat(complemento._id)
            toastr.success(response.data.message)
            await _load()
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No pude consultar el estado en el SAT.')
        } finally {
            setLoading(false)
        }
    }

    const _cancelComplemento = async () => {        setLoading(true)
        try {
            const response = await cancelarComplementoConValidacion({
                complementoId: complementoToCancel?._id,
                cancelReason,
                folioSustitucion
            })

            if (!response.data.success) {
                toastr.error(response.data.message || 'No se pudo cancelar el complemento')
                return
            }

            setComplementos((currentComplementos) => currentComplementos.map((complemento) => {
                if (complemento._id !== complementoToCancel._id) {
                    return complemento
                }

                return {
                    ...complemento,
                    status: response.data.data?.complemento?.status || 'cancelado'
                }
            }))

            setCancelModalOpen(false)
            toastr.success('Complemento cancelado correctamente')
        } catch (error) {
            toastr.error(error.response?.data?.message || 'No se pudo cancelar el complemento')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Complementos de Pago" breadcrumbItem="Complementos" />

                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col>
                                        <h4 className="card-title">
                                            Complementos de Pago
                                            <small className="text-muted ml-2">({total} registros)</small>
                                        </h4>
                                    </Col>
                                    <Col className="text-right">
                                        <Link to="/complementos/nuevo">
                                            <Button color="primary" size="sm">
                                                <i className="bx bx-plus mr-1"></i>
                                                Nuevo Complemento
                                            </Button>
                                        </Link>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <Table className="table-centered table-nowrap mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th></th>
                                                <th>UUID</th>
                                                <th>Receptor</th>
                                                <th>RFC</th>
                                                <th>Fecha de Pago</th>
                                                <th>Forma de Pago</th>
                                                <th>Moneda</th>
                                                <th>Monto</th>
                                                <th>Estatus</th>
                                                <th>Opciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {complementos.length === 0 && (
                                                <tr>
                                                    <td colSpan={10} className="text-center text-muted py-4">
                                                        No hay complementos de pago registrados.
                                                    </td>
                                                </tr>
                                            )}
                                            {complementos.map(c => (
                                                <React.Fragment key={c._id}>
                                                <tr>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-link p-0"
                                                            title="Ver historial"
                                                            onClick={() => _toggleHistory(c._id)}
                                                        >
                                                            <i className={`bx ${expandedId === c._id ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        {c.uuid?.toUpperCase() || '—'}
                                                        {c.fuenteImportacion === 'xml_externo' && <span className="badge badge-light ml-2">XML externo</span>}
                                                        {c.importadoDelSAT === true && <span className="badge badge-light ml-2">SAT</span>}
                                                    </td>
                                                    <td>{c.receptor?.razonSocial || '—'}</td>
                                                    <td>{c.receptor?.rfc || '—'}</td>
                                                    <td>
                                                        {c.fechaPago
                                                            ? <Moment format="DD/MM/YYYY">{c.fechaPago}</Moment>
                                                            : '—'}
                                                    </td>
                                                    <td>{c.formaPago || '—'}</td>
                                                    <td>{c.moneda || '—'}</td>
                                                    <td>
                                                        <CurrencyFormat
                                                            value={c.monto || 0}
                                                            displayType="text"
                                                            thousandSeparator=","
                                                            decimalSeparator="."
                                                            decimalScale={2}
                                                            fixedDecimalScale
                                                            prefix="$"
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${c.status === 'timbrado' ? 'success' : 'secondary'}`}>
                                                            {c.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <UncontrolledDropdown className="text-right">
                                                            <DropdownToggle tag="a" className="card-drop" href="#">
                                                                <i className="mdi mdi-dots-horizontal font-size-18"></i>
                                                            </DropdownToggle>
                                                            <DropdownMenu right>
                                                                <DropdownItem onClick={() => _downloadPDF(c._id, c.uuid)}>
                                                                    <i className="bx bxs-file-pdf font-size-16 text-danger mr-2"></i>
                                                                    Descargar PDF
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => _downloadXML(c._id, c.uuid)}>
                                                                    <i className="bx bxs-file font-size-16 text-success mr-2"></i>
                                                                    Descargar XML
                                                                </DropdownItem>
                                                                {c.fuenteImportacion === 'xml_externo' && (
                                                                    <>
                                                                        <div className="dropdown-divider"></div>
                                                                        <DropdownItem onClick={() => _verifySatStatus(c)}>
                                                                            <i className="mdi mdi-shield-check font-size-16 text-success mr-2"></i>
                                                                            Actualizar estado en SAT
                                                                        </DropdownItem>
                                                                    </>
                                                                )}
                                                                {c.importadoDelSAT === true && (
                                                                    <>
                                                                        <div className="dropdown-divider"></div>
                                                                        <DropdownItem onClick={() => _verEnEgresos(c)}>
                                                                            <i className="mdi mdi-receipt font-size-16 text-primary mr-2"></i>
                                                                            Ver en Egresos
                                                                        </DropdownItem>
                                                                    </>
                                                                )}
                                                                {c.importadoDelSAT !== true && c.fuenteImportacion !== 'xml_externo' && c.status !== 'cancelado' && (
                                                                    <>
                                                                        <div className="dropdown-divider"></div>
                                                                        <DropdownItem onClick={() => _openCancelModal(c)}>
                                                                            <i className="mdi mdi-cancel font-size-16 text-danger mr-2"></i>
                                                                            Cancelar
                                                                        </DropdownItem>
                                                                    </>
                                                                )}
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                    </td>
                                                </tr>
                                                {expandedId === c._id && (
                                                    <tr>
                                                        <td colSpan={10} className="bg-light py-2 px-4">
                                                            <small className="text-muted font-weight-bold d-block mb-1">Historial</small>
                                                            {(bitacoraMap[c._id] || []).length === 0 ? (
                                                                <small className="text-muted">Sin historial registrado</small>
                                                            ) : (
                                                                (bitacoraMap[c._id] || []).map((ev, i) => (
                                                                    <div key={i} className="d-flex align-items-center mb-1">
                                                                        <small className="text-muted mr-2" style={{ minWidth: 110 }}>
                                                                            <Moment format="DD/MM/YY HH:mm">{ev.createdAt}</Moment>
                                                                        </small>
                                                                        <span className="badge badge-info mr-2" style={{ fontSize: '0.7rem' }}>
                                                                            {ev.tipo}
                                                                        </span>
                                                                        <small>{ev.descripcion}</small>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {totalPages > 1 && (
                                    <Row className="mt-3">
                                        <Col className="d-flex justify-content-center align-items-center">
                                            <Button
                                                color="outline-secondary"
                                                size="sm"
                                                disabled={page <= 1}
                                                onClick={() => setPage(p => p - 1)}
                                                className="mr-2"
                                            >
                                                Anterior
                                            </Button>
                                            <span className="mx-3 text-muted">
                                                Página {page} de {totalPages}
                                            </span>
                                            <Button
                                                color="outline-secondary"
                                                size="sm"
                                                disabled={page >= totalPages}
                                                onClick={() => setPage(p => p + 1)}
                                            >
                                                Siguiente
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {cancelModalOpen && (
                <CancelacionCFDIModal
                    title={`Cancelar Complemento ${complementoToCancel?.folio || ''}`}
                    isOpen={cancelModalOpen}
                    cancelReason={cancelReason}
                    folioSustitucion={folioSustitucion}
                    showFolioSustitucion={showFolioSustitucion}
                    onCancelReasonChange={(value) => {
                        setCancelReason(value)

                        if (value?.value === '01') {
                            setShowFolioSustitucion(true)
                            return
                        }

                        setShowFolioSustitucion(false)
                        setFolioSustitucion('')
                    }}
                    onFolioSustitucionChange={setFolioSustitucion}
                    onConfirm={_cancelComplemento}
                    onRequestClose={() => setCancelModalOpen(false)}
                    pagos20={true}
                />
            )}
        </div>
    )
}

export default ComplementosIndex
